import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import * as duckdb from "@duckdb/duckdb-wasm";
import { FaDatabase, FaTable, FaSpinner, FaPlay, FaChartLine, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { supabase } from "../../lib/supabase";
import Editor, { loader } from '@monaco-editor/react';
import posthog from 'posthog-js';
import { useTheme } from "../../context/ThemeContext";
import { DATA_VERSION } from "../../config/dataVersion";
import { validateSQLResult } from "../../utils/sqlValidation";
import Skeleton from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';

// Helper function to format display values (especially Unix timestamps)
function formatDisplayValue(val) {
  if (val === null || val === undefined) return null;

  let numVal = typeof val === 'bigint' ? Number(val) : val;

  if (typeof numVal === 'string' && !isNaN(numVal)) {
    numVal = Number(numVal);
  }

  // Detect Unix timestamp (seconds or milliseconds)
  if (typeof numVal === 'number' && numVal > 1000000000 && numVal < 10000000000000) {
    const timestamp = numVal > 10000000000 ? numVal : numVal * 1000;
    const date = new Date(timestamp);

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  return String(val);
}

// Memoized results table component to prevent re-renders during typing
const ResultsTable = React.memo(({ results, error, loading, displayLimit, setDisplayLimit, onToggleChart, showChart, title = "Query Results", subtitle, executionTime }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // Detect if results are suitable for charting (has month/date column and numeric column)
  const isChartable = useMemo(() => {
    if (!results || results.length === 0) return false;
    const columns = Object.keys(results[0]);
    // console.log('📊 Chart Detection - Columns:', columns);

    const hasDateColumn = columns.some(col =>
      col.toLowerCase().includes('month') ||
      col.toLowerCase().includes('year') ||
      col.toLowerCase().includes('date')
    );

    const hasNumericColumn = columns.some(col => {
      const value = results[0][col];
      return typeof value === 'number' || typeof value === 'bigint';
    });

    const chartable = hasDateColumn && hasNumericColumn && results.length > 1;
    return chartable;
  }, [results]);

  // ... (chart data logic remains same)
  const chartData = useMemo(() => {
    if (!isChartable || !results) return null;

    const columns = Object.keys(results[0]);
    const dateColumn = columns.find(col =>
      col.toLowerCase().includes('month') ||
      col.toLowerCase().includes('year') ||
      col.toLowerCase().includes('date')
    );
    const numericColumns = columns.filter(col => {
      const value = results[0][col];
      return typeof value === 'number' || typeof value === 'bigint';
    });

    return {
      labels: results.map(row => row[dateColumn]),
      datasets: numericColumns.map((col, idx) => ({
        label: col,
        data: results.map(row => {
          const val = row[col];
          return typeof val === 'bigint' ? Number(val) : val;
        }),
        borderColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'][idx % 4],
        backgroundColor: ['#3b82f620', '#10b98120', '#f59e0b20', '#ef444420'][idx % 4],
        tension: 0.3
      }))
    };
  }, [isChartable, results]);

  // ... (chart effect remains same)
  // Initialize or update chart when data changes
  useEffect(() => {
    if (showChart && chartData && chartRef.current && window.Chart) {
      if (chartInstanceRef.current) chartInstanceRef.current.destroy();
      const ctx = chartRef.current.getContext('2d');
      chartInstanceRef.current = new window.Chart(ctx, {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: { legend: { display: true, position: 'top' }, title: { display: true, text: 'Time Series Analysis' } },
          scales: {
            x: {
              ticks: {
                maxRotation: 55,
                minRotation: 0,
                autoSkip: true,
                maxTicksLimit: 20,
              }
            },
            y: { beginAtZero: true, ticks: { precision: 0 } }
          }
        }
      });
    }
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [showChart, chartData]);

  // ... (loading/error states remain same)
  if (loading) {
    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col overflow-hidden">
        <div className="bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-3 flex gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-4 w-24 bg-gray-200 dark:bg-slate-700" />)}
        </div>
        <div className="p-4 space-y-4">
          {[1, 2, 3, 4, 5, 6].map(row => (
            <div key={row} className="flex gap-4">
              <Skeleton className="h-4 w-8 bg-gray-100 dark:bg-slate-700/50" />
              <Skeleton className="h-4 w-32 bg-gray-100 dark:bg-slate-700/50" />
              <Skeleton className="h-4 w-48 bg-gray-100 dark:bg-slate-700/50" />
              <Skeleton className="h-4 w-24 bg-gray-100 dark:bg-slate-700/50" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg shadow-sm border border-red-200 dark:border-red-900/50 h-full flex flex-col">
        <div className="bg-red-50 dark:bg-red-900/20 px-4 py-3 border-b border-red-200 dark:border-red-900/50 flex-shrink-0 flex items-start gap-3">
          <div className="text-red-500 mt-1"><FaTimesCircle size={18} /></div>
          <div>
            <h3 className="text-red-900 dark:text-red-200 font-bold text-lg">Query Failed</h3>
            <p className="text-red-700 dark:text-red-300 text-xs mt-0.5">Please check your SQL syntax</p>
          </div>
        </div>
        <div className="flex-1 p-6 overflow-auto">
          <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-md border border-red-100 dark:border-red-900/50">
            <pre className="text-red-800 dark:text-red-300 font-mono text-sm whitespace-pre-wrap break-words leading-relaxed">{error}</pre>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <EmptyState
        icon={FaPlay}
        title="Ready to Execute"
        description={<>Write your SQL query in the editor above and click <span className="font-bold">Run</span> to see the results here.</>}
        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700"
      />
    );
  }

  if (results.length === 0) {
    return (
      <EmptyState
        icon={FaTable}
        title="No Results Found"
        description={<>Query executed successfully but returned 0 rows.{executionTime && <span className="block mt-1 text-xs text-gray-400">Time: {executionTime}s</span>}</>}
        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700"
      />
    );
  }

  const displayedRows = results.slice(0, displayLimit);
  const hasMore = results.length > displayLimit;

  return (
    <div className="bg-white dark:bg-[#1e293b] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden h-full flex flex-col">
      {/* Chart View */}
      {showChart && chartData ? (
        <div className="p-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
            <canvas ref={chartRef} height="80"></canvas>
          </div>
        </div>
      ) : (
        /* Table View */
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-x-auto overflow-y-auto bg-white dark:bg-slate-900 border-y border-gray-200 dark:border-slate-700">
            <table className="table-auto w-full min-w-[600px] border-b border-gray-200 dark:border-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800 border-b-2 border-gray-200 dark:border-slate-600 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-300 uppercase tracking-wider border-r border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                    #
                  </th>
                  {Object.keys(results[0]).map((key, index) => (
                    <th key={`col-${index}`} className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-slate-300 uppercase tracking-wider border-r border-gray-200 dark:border-slate-700 last:border-r-0 whitespace-nowrap bg-gray-50 dark:bg-slate-800">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {displayedRows.map((row, idx) => (
                  <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-gray-50/50 dark:bg-slate-800/50'} hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors`}>
                    <td className="px-4 py-2.5 text-xs text-gray-400 dark:text-slate-500 font-medium border-r border-gray-100 dark:border-slate-800">
                      {idx + 1}
                    </td>
                    {Object.values(row).map((val, i) => {
                      const displayValue = formatDisplayValue(val);
                      return (
                        <td key={i} className="px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 border-r border-gray-100 dark:border-slate-800 last:border-r-0 font-mono whitespace-nowrap">
                          {displayValue !== null ? displayValue : <span className="text-gray-400 dark:text-slate-600 italic text-xs">null</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="h-24"></div>
          </div>
          <div className="px-4 py-2 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 flex justify-between items-center flex-shrink-0">
            <div className="flex items-center gap-3 text-xs font-mono">
              <span className="text-gray-900 dark:text-gray-300 font-semibold">
                {results.length} {results.length === 1 ? 'row' : 'rows'}
              </span>
              {executionTime && (
                <span className="text-gray-500 dark:text-gray-400 border-l border-gray-300 dark:border-gray-600 pl-3">
                  {executionTime}s
                </span>
              )}
              {hasMore && <span className="text-gray-400 dark:text-gray-500">(showing first {displayLimit})</span>}
            </div>
            <div className="flex items-center gap-3">
              {isChartable && (
                <button
                  onClick={onToggleChart}
                  className={`text-xs flex items-center gap-1 ${showChart ? 'text-blue-500 font-bold' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  title="Toggle Chart View"
                >
                  <FaChartLine /> Chart
                </button>
              )}
              {hasMore && (
                <button
                  onClick={() => setDisplayLimit(prev => prev + 100)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 hover:underline font-medium"
                >
                  Load More
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});



ResultsTable.displayName = 'ResultsTable';

// Configure Monaco to load SQL language
loader.init().then(monaco => {
  // Register SQL language if not already registered
  const languages = monaco.languages.getLanguages();
  const sqlLang = languages.find(lang => lang.id === 'sql');

  if (!sqlLang) {
    monaco.languages.register({ id: 'sql' });
  }

  // Set SQL keywords including multi-word ones
  monaco.languages.setMonarchTokensProvider('sql', {
    defaultToken: '',
    tokenPostfix: '.sql',
    ignoreCase: true,

    keywords: [
      'ABORT', 'ACTION', 'ADD', 'AFTER', 'ALL', 'ALTER', 'ANALYZE', 'AND', 'AS', 'ASC',
      'ATTACH', 'AUTOINCREMENT', 'BEFORE', 'BEGIN', 'BETWEEN', 'BY', 'CASCADE', 'CASE',
      'CAST', 'CHECK', 'COLLATE', 'COLUMN', 'COMMIT', 'CONFLICT', 'CONSTRAINT', 'CREATE',
      'CROSS', 'CURRENT_DATE', 'CURRENT_TIME', 'CURRENT_TIMESTAMP', 'DATABASE', 'DEFAULT',
      'DEFERRABLE', 'DEFERRED', 'DELETE', 'DESC', 'DETACH', 'DISTINCT', 'DROP', 'EACH',
      'ELSE', 'END', 'ESCAPE', 'EXCEPT', 'EXCLUSIVE', 'EXISTS', 'EXPLAIN', 'FAIL', 'FOR',
      'FOREIGN', 'FROM', 'FULL', 'GLOB', 'GROUP', 'HAVING', 'IF', 'IGNORE', 'IMMEDIATE',
      'IN', 'INDEX', 'INDEXED', 'INITIALLY', 'INNER', 'INSERT', 'INSTEAD', 'INTERSECT',
      'INTO', 'IS', 'ISNULL', 'JOIN', 'KEY', 'LEFT', 'LIKE', 'LIMIT', 'MATCH', 'NATURAL',
      'NO', 'NOT', 'NOTNULL', 'NULL', 'OF', 'OFFSET', 'ON', 'OR', 'ORDER', 'OUTER', 'PLAN',
      'PRAGMA', 'PRIMARY', 'QUERY', 'RAISE', 'RECURSIVE', 'REFERENCES', 'REGEXP', 'REINDEX',
      'RELEASE', 'RENAME', 'REPLACE', 'RESTRICT', 'RIGHT', 'ROLLBACK', 'ROW', 'SAVEPOINT',
      'SELECT', 'SET', 'TABLE', 'TEMP', 'TEMPORARY', 'THEN', 'TO', 'TRANSACTION', 'TRIGGER',
      'UNION', 'UNIQUE', 'UPDATE', 'USING', 'VACUUM', 'VALUES', 'VIEW', 'VIRTUAL', 'WHEN',
      'WHERE', 'WITH', 'WITHOUT', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX'
    ],

    operators: [
      '=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=', '<>',
      '+', '-', '*', '/', '%', '&', '|', '^', '||'
    ],

    builtinFunctions: [
      'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'ABS', 'LENGTH', 'SUBSTR', 'TRIM',
      'LOWER', 'UPPER', 'REPLACE', 'CAST', 'COALESCE', 'NULLIF',
      'DATE_TRUNC', 'ROUND', 'CONCAT', 'SUBSTRING', 'STRFTIME', 'DATE',
      'FLOOR', 'CEIL', 'POWER', 'SQRT', 'MOD'
    ],

    tokenizer: {
      root: [
        { include: '@comments' },
        { include: '@whitespace' },
        { include: '@numbers' },
        { include: '@strings' },
        { include: '@complexIdentifiers' },
        [/[;,.]/, 'delimiter'],
        [/[()]/, '@brackets'],
        [/[\w@#$]+/, {
          cases: {
            '@keywords': 'keyword',
            '@operators': 'operator',
            '@builtinFunctions': 'predefined',
            '@default': 'identifier'
          }
        }],
      ],

      whitespace: [[/\s+/, 'white']],

      comments: [
        [/--+.*/, 'comment'],
        [/\/\*/, { token: 'comment.quote', next: '@comment' }]
      ],

      comment: [
        [/[^*/]+/, 'comment'],
        [/\*\//, { token: 'comment.quote', next: '@pop' }],
        [/./, 'comment']
      ],

      numbers: [
        [/0[xX][0-9a-fA-F]*/, 'number'],
        [/[$][+-]*\d*(\.\d*)?/, 'number'],
        [/((\d+(\.\d*)?)|(\.\d+))([eE][+-]?\d+)?/, 'number']
      ],

      strings: [
        [/'/, { token: 'string', next: '@string' }],
        [/"/, { token: 'string.double', next: '@stringDouble' }]
      ],

      string: [
        [/[^']+/, 'string'],
        [/''/, 'string'],
        [/'/, { token: 'string', next: '@pop' }]
      ],

      stringDouble: [
        [/[^"]+/, 'string.double'],
        [/""/, 'string.double'],
        [/"/, { token: 'string.double', next: '@pop' }]
      ],

      complexIdentifiers: [
        [/\[/, { token: 'identifier.quote', next: '@bracketedIdentifier' }],
        [/`/, { token: 'identifier.quote', next: '@quotedIdentifier' }]
      ],

      bracketedIdentifier: [
        [/[^\]]+/, 'identifier'],
        [/]]/, 'identifier'],
        [/]/, { token: 'identifier.quote', next: '@pop' }]
      ],

      quotedIdentifier: [
        [/[^`]+/, 'identifier'],
        [/``/, 'identifier'],
        [/`/, { token: 'identifier.quote', next: '@pop' }]
      ]
    }
  });
});

let db;
let conn;

// IndexedDB helper for caching dataset files
const CACHE_DB_NAME = 'duckdb_dataset_cache';
const CACHE_STORE_NAME = 'datasets';

async function openCacheDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(CACHE_DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(CACHE_STORE_NAME)) {
        db.createObjectStore(CACHE_STORE_NAME, { keyPath: 'key' });
      }
    };
  });
}

async function getCachedData(key) {
  const db = await openCacheDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CACHE_STORE_NAME], 'readonly');
    const store = transaction.objectStore(CACHE_STORE_NAME);
    const request = store.get(key);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result?.data);
  });
}

async function setCachedData(key, data) {
  const db = await openCacheDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CACHE_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(CACHE_STORE_NAME);
    const request = store.put({ key, data, timestamp: Date.now() });
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function initDuckDB() {
  if (db && conn) return { db, conn };

  // Self-hosted files in public/duckdb
  // We must use ABSOLUTE URLs because the worker runs in a Blob URL context 
  // and cannot resolve relative paths like "/duckdb/..."
  const getAbsoluteUrl = (path) => new URL(path, window.location.origin).toString();

  const UNPKG_BUNDLES = {
    mvp: {
      mainModule: getAbsoluteUrl("/duckdb/duckdb-mvp.wasm"),
      mainWorker: getAbsoluteUrl("/duckdb/duckdb-browser-mvp.worker.js"),
    },
    eh: {
      mainModule: getAbsoluteUrl("/duckdb/duckdb-eh.wasm"),
      mainWorker: getAbsoluteUrl("/duckdb/duckdb-browser-eh.worker.js"),
    },
  };

  try {
    const bundle = await duckdb.selectBundle(UNPKG_BUNDLES);

    // Fetch the worker script and create a blob URL
    // (Required for some environments to resolve relative paths correctly in workers)
    const workerResponse = await fetch(bundle.mainWorker);
    const workerBlob = await workerResponse.blob();
    const workerUrl = URL.createObjectURL(workerBlob);

    const worker = new Worker(workerUrl);
    const logger = new duckdb.ConsoleLogger();

    db = new duckdb.AsyncDuckDB(logger, worker);
    await db.instantiate(bundle.mainModule, bundle.pthreadWorker);

    conn = await db.connect();

    return { db, conn };
  } catch (error) {
    console.error("DuckDB initialization error:", error);
    throw error;
  }
}

// Helper function to find the query at cursor position (between semicolons)
function getQueryAtCursor(fullText, cursorOffset) {
  // Find the start of the current statement (after previous `;` or start of file)
  let startIdx = 0;
  for (let i = cursorOffset - 1; i >= 0; i--) {
    if (fullText[i] === ';') {
      startIdx = i + 1;
      break;
    }
  }

  // Find the end of the current statement (next `;` or end of file)
  let endIdx = fullText.length;
  for (let i = cursorOffset; i < fullText.length; i++) {
    if (fullText[i] === ';') {
      endIdx = i;
      break;
    }
  }

  // Extract and trim the query
  const queryText = fullText.substring(startIdx, endIdx).trim();
  return queryText;
}

export const SQLEditor = React.forwardRef(({
  defaultQuery = "",
  onVerify = null,
  isVerifying = false,
  isCompleted = false,
  datasetFiles = [], // Array of filenames or objects {bucket, name, tablePrefix}
  supabaseBucket = "datahelix-public-datasets", // Default bucket
  cacheKey // New prop for query persistence
}, ref) => {
  console.log('SQLEditor Render:', { isVerifying });
  const { theme } = useTheme();
  // Detect OS for shortcut symbol
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  const shortcutSymbol = isMac ? '⌘' : 'Ctrl';

  const [query, setQuery] = useState(defaultQuery);
  const [results, setResults] = useState(null);
  const [executionTime, setExecutionTime] = useState(null);
  const [displayLimit, setDisplayLimit] = useState(100); // Only display 100 rows by default
  const [showChart, setShowChart] = useState(false); // Toggle between table and chart view
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  // datasets is now defined as useMemo below (line ~554)
  const [loadedTables, setLoadedTables] = useState(new Set());
  const [loadingDataset, setLoadingDataset] = useState(null);
  const [tableSchemas, setTableSchemas] = useState({});
  const [tableSampleData, setTableSampleData] = useState({});
  // Resizable editor
  const [editorHeight, setEditorHeight] = useState(280);
  const dragStartY = useRef(null);
  const dragStartHeight = useRef(null);
  const isDragging = useRef(false);

  const [activeTab, setActiveTab] = useState('results'); // Default to 'results' per user request
  const [allTablesLoaded, setAllTablesLoaded] = useState(false);
  // Use refs to track state for non-reactive callbacks (like Monaco actions)
  const allTablesLoadedRef = useRef(allTablesLoaded);
  const loadingRef = useRef(false); // Track loading state to prevent concurrent queries

  // Sync refs with state
  useEffect(() => {
    allTablesLoadedRef.current = allTablesLoaded;
  }, [allTablesLoaded]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const editorRef = useRef(null);
  const containerRef = useRef(null);
  // Store connection refs to ensure both button and keyboard handlers use the same connection
  // This prevents HMR-related issues where module-level variables can become stale
  const connRef = useRef(null);
  const dbRef = useRef(null);
  const [useStrictValidation] = useState(true);

  // Track if query change is internal (restoring from cache) to avoid overwriting
  const isInternalChange = useRef(false);

  // Expose validation method to parent via ref
  React.useImperativeHandle(ref, () => ({
    validateQuery: async (solutionQuery) => {
      try {
        if (!conn) throw new Error("Database not initialized");
        if (!query || !query.trim()) throw new Error("Please write a query first");

        setLoading(true);

        // 1. Run User Query
        const userResult = await conn.query(query);
        const userRows = userResult.toArray().map(row => row.toJSON());

        // 2. Run Solution Query (Silent)
        const solutionResult = await conn.query(solutionQuery);
        const solutionRows = solutionResult.toArray().map(row => row.toJSON());

        // 3. Compare Results
        const validation = validateSQLResult(userRows, solutionRows, {
          checkColumnNames: useStrictValidation,
          checkOrder: false, // We allow loose ordering unless strictly required
          userQuery: query,
          solutionQuery: solutionQuery
        });

        setLoading(false);
        return validation;

      } catch (err) {
        setLoading(false);
        console.error("Validation error:", err);
        return {
          passed: false,
          message: "Error running validation: " + err.message
        };
      }
    },
    // Also expose simple execution
    // Also expose simple execution
    executeQuery: () => {
      executeQuery();
    }
  }));

  // Persistence Logic: Load query on key change, clear results
  useEffect(() => {
    // Clear results/errors/chart when switching challenges
    setResults(null);
    setError(null);
    setExecutionTime(null);
    setShowChart(false);
    setActiveTab('results');

    if (cacheKey) {
      const saved = localStorage.getItem(cacheKey);
      const isStaleDefault = saved === '-- Write your SQL query here\n' || saved === '-- Write your SQL query here';
      if (saved && !isStaleDefault) {
        setQuery(saved);
        isInternalChange.current = true; // Prevent overwriting with default
        return;
      }
    }
    // Fallback to default if no valid saved query
    setQuery(defaultQuery);
  }, [cacheKey, defaultQuery]);

  // Persistence Logic: Save query on change (Debounced)
  useEffect(() => {
    if (!cacheKey) return;

    const timeout = setTimeout(() => {
      localStorage.setItem(cacheKey, query);
    }, 500);

    return () => clearTimeout(timeout);
  }, [query, cacheKey]);

  // Manual ResizeObserver for Monaco layout - prevents ResizeObserver loop
  useEffect(() => {
    const container = containerRef.current;
    const editor = editorRef.current;

    if (!container || !editor) return;

    let isHandling = false;
    let rafId = null;

    const observer = new ResizeObserver((entries) => {
      // Cancel any pending RAF to debounce rapid resize events
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      // Wrap EVERYTHING in RAF and try-catch to avoid ResizeObserver loop errors
      rafId = window.requestAnimationFrame(() => {
        try {
          if (!Array.isArray(entries) || !entries.length) {
            return;
          }

          // Prevent re-entrancy
          if (isHandling) return;

          isHandling = true;

          try {
            editor.layout();
          } finally {
            isHandling = false;
          }
        } catch (error) {
          // Silently catch ResizeObserver errors to prevent them from bubbling
          if (!error.message?.includes('ResizeObserver')) {
            console.error('Editor layout error:', error);
          }
        }
      });
    });

    observer.observe(container);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      observer.disconnect();
    };
  }, []);

  // Configure Monaco completions when tables/schemas change
  useEffect(() => {
    initDuckDB()
      .then(() => {
        // Store in refs for HMR safety - both handlers will use these refs
        connRef.current = conn;
        dbRef.current = db;
        setDbReady(true);
      })
      .catch((err) => {
        setError("Failed to initialize database: " + err.message);
      });
  }, []);

  // Create a stable key for datasetFiles to avoid complex expression in useMemo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const datasetFilesKey = useMemo(() => JSON.stringify(datasetFiles), [datasetFiles]);

  // Initialize datasets from provided file list - use useMemo to prevent unnecessary re-initialization
  // This creates a stable reference that only changes when the actual files change
  const datasets = useMemo(() => {
    if (supabaseBucket && datasetFiles.length > 0) {
      const fileList = datasetFiles.map(filename => {
        // Support both string filenames and objects with tablePrefix
        if (typeof filename === 'string') {
          return { name: filename, bucket: supabaseBucket };
        }
        return { bucket: supabaseBucket, ...filename };
      });
      console.log('📦 Dataset initialization:', {
        bucket: supabaseBucket,
        fileCount: datasetFiles.length,
        files: fileList
      });
      return fileList;
    }
    return [];
  }, [supabaseBucket, datasetFiles]); // datasetFiles included to satisfy ESLint

  // Track if we've loaded these specific datasets to prevent reloading the same data
  const [loadedDatasetKey, setLoadedDatasetKey] = useState(null);
  const currentDatasetKey = useMemo(() =>
    `${supabaseBucket}_${datasetFilesKey}`,
    [supabaseBucket, datasetFilesKey]
  );

  // Setup Monaco editor completions
  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // Define custom theme with industry-standard SQL colors
    monaco.editor.defineTheme('sql-custom', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '569CD6', fontStyle: 'bold' }, // Blue for SQL keywords
        { token: 'type', foreground: '4EC9B0' }, // Teal for table names
        { token: 'variable', foreground: '9CDCFE' }, // Light blue for column names
        { token: 'string', foreground: 'CE9178' }, // Orange for strings
        { token: 'number', foreground: 'B5CEA8' }, // Light green for numbers
        { token: 'comment', foreground: '6A9955', fontStyle: 'italic' }, // Green for comments
        { token: 'predefined', foreground: 'DCDCAA' }, // Yellow for built-in functions
        { token: 'operator', foreground: 'D4D4D4' }, // Light gray for operators
      ],
      colors: {
        'editor.background': '#1E1E1E',
      }
    });

    monaco.editor.setTheme(theme === 'dark' ? 'sql-custom' : 'vs');

    // Update theme when context changes
    // Update theme when context changes

    // Add Cmd/Ctrl+Enter to run query
    editor.addAction({
      id: 'run-query',
      label: 'Run Query',
      keybindings: [
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter
      ],
      run: async () => {
        if (!dbRef.current || !connRef.current) {
          setError("Database not ready yet. Please wait...");
          return;
        }

        if (!allTablesLoadedRef.current) {
          setError("Tables are still loading... Please wait a moment.");
          return;
        }

        // Prevent concurrent query execution (can cause DuckDB connection issues)
        if (loadingRef.current) {
          return; // Silently ignore if already running a query
        }

        // Determine which query to execute:
        // 1. If text is selected, use the selection
        // 2. Otherwise, find the query at cursor position (between semicolons)
        const model = editor.getModel();
        const selection = editor.getSelection();
        let queryToExecute;

        // Check if there's a non-empty selection
        if (selection && !selection.isEmpty()) {
          queryToExecute = model.getValueInRange(selection);
        } else {
          // No selection - find query at cursor position
          const cursorPosition = editor.getPosition();
          if (cursorPosition && model) {
            const fullText = model.getValue();
            const cursorOffset = model.getOffsetAt(cursorPosition);
            queryToExecute = getQueryAtCursor(fullText, cursorOffset);
          } else {
            queryToExecute = model.getValue();
          }
        }

        if (!queryToExecute || !queryToExecute.trim()) {
          setError("No query to execute. Write a query or select text to run.");
          return;
        }

        // Validate query - block destructive operations
        const destructiveKeywords = ['DROP', 'DELETE', 'UPDATE', 'ALTER', 'TRUNCATE', 'INSERT'];

        for (const keyword of destructiveKeywords) {
          // Check for keyword as whole word (not part of another word)
          const regex = new RegExp(`\\b${keyword}\\b`, 'i');
          if (regex.test(queryToExecute)) {
            setError(`❌ Blocked: ${keyword} commands are not allowed. This playground is for SELECT queries only.`);
            return;
          }
        }

        setLoading(true);
        setError(null);
        setResults(null);
        setExecutionTime(null);
        setActiveTab('results'); // Switch effectively immediately when query starts

        const start = performance.now();

        try {
          const result = await connRef.current.query(queryToExecute);

          // Convert to Arrow table and then to plain objects
          // This avoids the proxy duplicate key issue
          const arrowTable = result;
          const numRows = arrowTable.numRows;
          const schema = arrowTable.schema;
          const columnNames = schema.fields.map(field => field.name);

          // Handle duplicate column names by adding suffix
          const seenNames = new Map();
          const uniqueColumnNames = columnNames.map(name => {
            if (seenNames.has(name)) {
              const count = seenNames.get(name) + 1;
              seenNames.set(name, count);
              return `${name}_${count}`;
            } else {
              seenNames.set(name, 1);
              return name;
            }
          });

          // Convert to plain objects
          const plainRows = [];
          for (let i = 0; i < numRows; i++) {
            const row = {};
            columnNames.forEach((colName, idx) => {
              const column = arrowTable.getChildAt(idx);
              row[uniqueColumnNames[idx]] = column?.get(i) ?? null;
            });
            plainRows.push(row);
          }

          const end = performance.now();
          const duration = ((end - start) / 1000).toFixed(2);

          setResults(plainRows);
          setExecutionTime(duration);
          setDisplayLimit(100); // Reset display limit on new query
          // toast.success(`Query Executed (${duration}s)`);

          posthog.capture('sql_query_executed', {
            status: 'success',
            duration: duration,
            rows: numRows,
            query_length: queryToExecute.length
          });
        } catch (err) {
          posthog.capture('sql_query_executed', {
            status: 'error',
            error: err.message,
            query_length: queryToExecute.length
          });
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    });
  }, [theme]);

  // Effect to update theme when it changes
  useEffect(() => {
    if (window.monaco) {
      window.monaco.editor.setTheme(theme === 'dark' ? 'sql-custom' : 'vs');
    }
  }, [theme]);

  // Update Monaco tokenizer when tables/schemas change (memoized)
  const tokenizerConfig = useMemo(() => {
    const tableNames = Array.from(loadedTables).map(t => t.toUpperCase());
    const allColumns = [];
    Object.values(tableSchemas).forEach(cols => allColumns.push(...cols.map(c => c.toUpperCase())));

    return {
      defaultToken: '',
      tokenPostfix: '.sql',
      ignoreCase: true,

      keywords: [
        'ABORT', 'ACTION', 'ADD', 'AFTER', 'ALL', 'ALTER', 'ANALYZE', 'AND', 'AS', 'ASC',
        'ATTACH', 'AUTOINCREMENT', 'BEFORE', 'BEGIN', 'BETWEEN', 'BY', 'CASCADE', 'CASE',
        'CAST', 'CHECK', 'COLLATE', 'COLUMN', 'COMMIT', 'CONFLICT', 'CONSTRAINT', 'CREATE',
        'CROSS', 'CURRENT_DATE', 'CURRENT_TIME', 'CURRENT_TIMESTAMP', 'DATABASE', 'DEFAULT',
        'DEFERRABLE', 'DEFERRED', 'DELETE', 'DESC', 'DETACH', 'DISTINCT', 'DROP', 'EACH',
        'ELSE', 'END', 'ESCAPE', 'EXCEPT', 'EXCLUSIVE', 'EXISTS', 'EXPLAIN', 'FAIL', 'FOR',
        'FOREIGN', 'FROM', 'FULL', 'GLOB', 'GROUP', 'HAVING', 'IF', 'IGNORE', 'IMMEDIATE',
        'IN', 'INDEX', 'INDEXED', 'INITIALLY', 'INNER', 'INSERT', 'INSTEAD', 'INTERSECT',
        'INTO', 'IS', 'ISNULL', 'JOIN', 'KEY', 'LEFT', 'LIKE', 'LIMIT', 'MATCH', 'NATURAL',
        'NO', 'NOT', 'NOTNULL', 'NULL', 'OF', 'OFFSET', 'ON', 'OR', 'ORDER', 'OUTER', 'PLAN',
        'PRAGMA', 'PRIMARY', 'QUERY', 'RAISE', 'RECURSIVE', 'REFERENCES', 'REGEXP', 'REINDEX',
        'RELEASE', 'RENAME', 'REPLACE', 'RESTRICT', 'RIGHT', 'ROLLBACK', 'ROW', 'SAVEPOINT',
        'SELECT', 'SET', 'TABLE', 'TEMP', 'TEMPORARY', 'THEN', 'TO', 'TRANSACTION', 'TRIGGER',
        'UNION', 'UNIQUE', 'UPDATE', 'USING', 'VACUUM', 'VALUES', 'VIEW', 'VIRTUAL', 'WHEN',
        'WHERE', 'WITH', 'WITHOUT', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX'
      ],

      tableNames: tableNames,
      columnNames: allColumns,

      operators: [
        '=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=', '<>',
        '+', '-', '*', '/', '%', '&', '|', '^', '||'
      ],

      builtinFunctions: [
        'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'ABS', 'LENGTH', 'SUBSTR', 'TRIM',
        'LOWER', 'UPPER', 'REPLACE', 'CAST', 'COALESCE', 'NULLIF'
      ],

      tokenizer: {
        root: [
          { include: '@comments' },
          { include: '@whitespace' },
          { include: '@numbers' },
          { include: '@strings' },
          { include: '@complexIdentifiers' },
          [/[;,.]/, 'delimiter'],
          [/[()]/, '@brackets'],
          [/[\w@#$]+/, {
            cases: {
              '@keywords': 'keyword',
              '@operators': 'operator',
              '@builtinFunctions': 'predefined',
              '@tableNames': 'type',
              '@columnNames': 'variable',
              '@default': 'identifier'
            }
          }],
        ],

        whitespace: [[/\s+/, 'white']],

        comments: [
          [/--+.*/, 'comment'],
          [/\/\*/, { token: 'comment.quote', next: '@comment' }]
        ],

        comment: [
          [/[^*/]+/, 'comment'],
          [/\*\//, { token: 'comment.quote', next: '@pop' }],
          [/./, 'comment']
        ],

        numbers: [
          [/0[xX][0-9a-fA-F]*/, 'number'],
          [/[$][+-]*\d*(\.\d*)?/, 'number'],
          [/((\d+(\.\d*)?)|(\.\d+))([eE][+-]?\d+)?/, 'number']
        ],

        strings: [
          [/'/, { token: 'string', next: '@string' }],
          [/"/, { token: 'string.double', next: '@stringDouble' }]
        ],

        string: [
          [/[^']+/, 'string'],
          [/''/, 'string'],
          [/'/, { token: 'string', next: '@pop' }]
        ],

        stringDouble: [
          [/[^"]+/, 'string.double'],
          [/""/, 'string.double'],
          [/"/, { token: 'string.double', next: '@pop' }]
        ],

        complexIdentifiers: [
          [/\[/, { token: 'identifier.quote', next: '@bracketedIdentifier' }],
          [/`/, { token: 'identifier.quote', next: '@quotedIdentifier' }]
        ],

        bracketedIdentifier: [
          [/[^\]]+/, 'identifier'],
          [/]]/, 'identifier'],
          [/]/, { token: 'identifier.quote', next: '@pop' }]
        ],

        quotedIdentifier: [
          [/[^`]+/, 'identifier'],
          [/``/, 'identifier'],
          [/`/, { token: 'identifier.quote', next: '@pop' }]
        ]
      }
    };
  }, [loadedTables, tableSchemas]);

  // Apply tokenizer config only when it changes
  useEffect(() => {
    if (!window.monaco || !editorRef.current || loadedTables.size === 0) return;

    const monaco = window.monaco;
    console.log('Updating tokenizer with', tokenizerConfig.tableNames.length, 'tables,', tokenizerConfig.columnNames.length, 'columns');

    monaco.languages.setMonarchTokensProvider('sql', tokenizerConfig);
  }, [tokenizerConfig, loadedTables.size]);

  // Update Monaco completions when tables/schemas change
  useEffect(() => {
    if (!editorRef.current) return;

    const monaco = window.monaco;
    if (!monaco) return;

    // Cache table/column data to avoid recreating on every keystroke
    const tableNamesCache = Array.from(loadedTables);
    const tableSchemasCache = { ...tableSchemas };

    // SQL Keywords and Functions (defined once, reused throughout)
    const SQL_KEYWORDS = [
      // Core query keywords
      'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN',
      'ORDER BY', 'GROUP BY', 'HAVING', 'LIMIT', 'OFFSET',
      'WITH', 'RECURSIVE',
      // Join types
      'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN', 'CROSS JOIN', 'FULL JOIN', 'FULL OUTER JOIN',
      'ON', 'USING',
      // Set operations
      'UNION', 'UNION ALL', 'INTERSECT', 'EXCEPT',
      // Subquery keywords
      'EXISTS', 'ANY', 'ALL', 'SOME',
      // Column/table operations
      'AS', 'DISTINCT', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'TRUNCATE',
      'INTO', 'VALUES', 'SET',
      // Conditional
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'IF', 'IFNULL', 'NULLIF',
      // Constraints
      'PRIMARY KEY', 'FOREIGN KEY', 'UNIQUE', 'CHECK', 'DEFAULT', 'NOT NULL',
      // Aggregate functions
      'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'STDDEV', 'VARIANCE',
      // Date/Time functions
      'DATE_TRUNC', 'DATE_PART', 'EXTRACT', 'NOW', 'CURRENT_DATE', 'CURRENT_TIME', 'CURRENT_TIMESTAMP',
      'DATE', 'TIME', 'TIMESTAMP', 'INTERVAL', 'AGE', 'DATE_ADD', 'DATE_SUB', 'DATEDIFF',
      'YEAR', 'MONTH', 'DAY', 'HOUR', 'MINUTE', 'SECOND',
      // String functions
      'CONCAT', 'CONCAT_WS', 'SUBSTRING', 'SUBSTR', 'LEFT', 'RIGHT', 'LENGTH', 'CHAR_LENGTH',
      'UPPER', 'LOWER', 'TRIM', 'LTRIM', 'RTRIM', 'REPLACE', 'REVERSE', 'REPEAT',
      'POSITION', 'LOCATE', 'INSTR', 'LPAD', 'RPAD', 'SPLIT_PART',
      // Math functions
      'ROUND', 'FLOOR', 'CEIL', 'CEILING', 'ABS', 'POWER', 'SQRT', 'MOD', 'SIGN',
      'RANDOM', 'RAND', 'EXP', 'LN', 'LOG', 'LOG10',
      // Type conversion
      'CAST', 'CONVERT', 'TO_CHAR', 'TO_DATE', 'TO_NUMBER', 'TO_TIMESTAMP',
      // Null handling
      'COALESCE', 'NULLIF', 'IFNULL', 'NVL', 'IS NULL', 'IS NOT NULL',
      // Window functions
      'OVER', 'PARTITION BY', 'ROW_NUMBER', 'RANK', 'DENSE_RANK', 'NTILE',
      'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE', 'NTH_VALUE',
      // Other common functions
      'GREATEST', 'LEAST', 'ARRAY_AGG', 'STRING_AGG', 'JSON_AGG', 'JSON_BUILD_OBJECT',
      'REGEXP_REPLACE', 'REGEXP_MATCHES', 'SIMILAR TO'
    ];

    const AGGREGATE_FUNCTIONS = ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'DISTINCT', 'AS'];

    // Helper: Create suggestion object
    const createSuggestion = (label, kind, insertText, range, sortText, description = '', detail = '') => ({
      label: typeof label === 'string' ? label : { label, description },
      kind,
      insertText,
      range,
      sortText,
      ...(detail && { detail }),
      ...(typeof label === 'string' && { filterText: label })
    });

    // Helper: Create column suggestions (both qualified and unqualified)
    const createColumnSuggestions = (tableName, columns, range, sortPrefix = '1') => {
      const suggestions = [];
      columns.forEach(col => {
        // Qualified: table.column
        suggestions.push(
          createSuggestion(
            col,
            monaco.languages.CompletionItemKind.Field,
            `${tableName}.${col}`,
            range,
            `${sortPrefix}_${tableName}_${col}`,
            `Column from ${tableName}`
          )
        );
        // Unqualified: column
        suggestions.push(
          createSuggestion(
            col,
            monaco.languages.CompletionItemKind.Field,
            col,
            range,
            `${parseInt(sortPrefix) + 1}_${col}_${tableName}`,
            `Column from ${tableName}`
          )
        );
      });
      return suggestions;
    };

    // Helper: Get accessible tables from current query context
    const getQueryContext = (model, position) => {
      const fullQuery = model.getValue();
      const textBeforeCursor = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column
      });

      // Strip comments to avoid false semantic matches
      const cleanText = textBeforeCursor
        .replace(/--.*/g, '') // Single line comments
        .replace(/\/\*[\s\S]*?\*\//g, ''); // Multi-line comments

      const selectCount = (cleanText.match(/\bSELECT\b/gi) || []).length;
      const fromCount = (cleanText.match(/\bFROM\b/gi) || []).length;
      const inSelectClause = selectCount > fromCount;

      if (!inSelectClause) return { context: 'other', tables: [] };

      const afterLastSelect = cleanText.split(/\bSELECT\b/gi).pop() || '';
      if (/\b(WHERE|ON|HAVING)\b/i.test(afterLastSelect)) {
        return { context: 'condition', tables: [] };
      }

      // Extract all tables from FROM and JOIN clauses
      const tables = new Set();
      const tablePattern = /\b(?:FROM|(?:LEFT|RIGHT|INNER|OUTER|FULL|CROSS)\s+JOIN)\s+(\w+)/gi;
      let match;

      while ((match = tablePattern.exec(fullQuery)) !== null) {
        const tableName = match[1].toLowerCase();
        if (tableNamesCache.includes(tableName)) {
          tables.add(tableName);
        }
      }

      return { context: 'select', tables: Array.from(tables) };
    };

    // Main completion provider
    const disposable = monaco.languages.registerCompletionItemProvider('sql', {
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };

        const currentLine = model.getLineContent(position.lineNumber);
        const textBefore = currentLine.substring(0, position.column - 1);
        const textBeforeUpper = textBefore.toUpperCase();

        // 1. Dot-completion: table.column
        const tableDotMatch = textBefore.match(/(\w+)\.(\w*)$/);
        if (tableDotMatch) {
          const tableName = tableDotMatch[1].toLowerCase();
          const columns = tableSchemasCache[tableName];
          if (!columns) return { suggestions: [] };

          return {
            suggestions: columns.slice(0, 50).map(col =>
              createSuggestion(col, monaco.languages.CompletionItemKind.Field, col, range, `0_${col}`, `Column from ${tableName}`)
            )
          };
        }

        // 2. After FROM/JOIN: suggest tables
        if (/\b(FROM|(?:LEFT|RIGHT|INNER|OUTER|FULL|CROSS)\s+JOIN)\s+\w*$/i.test(textBeforeUpper)) {
          return {
            suggestions: tableNamesCache.map(table =>
              createSuggestion(table, monaco.languages.CompletionItemKind.Class, table, range, `0_${table}`, 'Table')
            )
          };
        }

        const { context, tables: accessibleTables } = getQueryContext(model, position);

        // 3. SELECT clause context
        if (context === 'select') {
          const suggestions = [];

          // No FROM yet: suggest ALL SQL keywords + aggregates
          if (accessibleTables.length === 0) {
            // Add all SQL keywords (includes SELECT, FROM, WHERE, etc.)
            SQL_KEYWORDS.forEach(kw => {
              suggestions.push(createSuggestion(kw, monaco.languages.CompletionItemKind.Keyword, kw, range, `0_${kw}`, 'SQL Keyword'));
            });
            // Add aggregate functions
            AGGREGATE_FUNCTIONS.forEach(fn => {
              suggestions.push(createSuggestion(fn, monaco.languages.CompletionItemKind.Function, fn, range, `1_${fn}`, 'SQL Function'));
            });
            return { suggestions };
          }

          // FROM exists: suggest columns from accessible tables
          accessibleTables.forEach(tableName => {
            const columns = tableSchemasCache[tableName];
            if (columns) {
              suggestions.push(...createColumnSuggestions(tableName, columns, range, '1'));
            }
          });

          // Add aggregate functions
          AGGREGATE_FUNCTIONS.forEach(fn => {
            suggestions.push(createSuggestion(fn, monaco.languages.CompletionItemKind.Function, fn, range, `3_${fn}`, 'SQL Function'));
          });

          // Allow all keywords in SELECT clause too (e.g. CASE, CAST, AS)
          SQL_KEYWORDS.forEach(kw => {
            suggestions.push(createSuggestion(kw, monaco.languages.CompletionItemKind.Keyword, kw, range, `4_${kw}`, 'SQL Keyword'));
          });

          return { suggestions };
        }

        // 4. Condition clause context (WHERE/ON/HAVING)
        if (context === 'condition' && accessibleTables.length > 0) {
          const suggestions = [];

          accessibleTables.forEach(tableName => {
            const columns = tableSchemasCache[tableName];
            if (columns) {
              suggestions.push(...createColumnSuggestions(tableName, columns, range, '1'));
            }
          });

          // In condition clause: suggest columns AND all keywords (LIMIT, GROUP BY, AND, OR...)
          // We previously restricted this to just logical operators, which blocked clause-switching keywords
          SQL_KEYWORDS.forEach(kw => {
            suggestions.push(createSuggestion(kw, monaco.languages.CompletionItemKind.Keyword, kw, range, `3_${kw}`, 'SQL Keyword'));
          });

          return { suggestions };
        }

        // 5. Default: comprehensive suggestions
        const suggestions = [];
        const currentWord = word.word.toUpperCase();

        // Keywords with prefix matching priority
        SQL_KEYWORDS.forEach(kw => {
          const matchScore = kw.startsWith(currentWord) ? 0 : 1;
          suggestions.push(createSuggestion(kw, monaco.languages.CompletionItemKind.Keyword, kw, range, `${matchScore}_a_${kw}`, '', 'SQL Keyword'));
        });

        // Tables
        tableNamesCache.forEach(table => {
          const matchScore = table.toLowerCase().startsWith(word.word.toLowerCase()) ? 0 : 1;
          suggestions.push(createSuggestion(table, monaco.languages.CompletionItemKind.Class, table, range, `${matchScore}_b_${table}`, '', 'Table'));
        });

        // All columns (for convenience)
        Object.entries(tableSchemasCache).forEach(([table, columns]) => {
          columns.forEach(col => {
            const matchScore = col.toLowerCase().startsWith(word.word.toLowerCase()) ? 0 : 1;
            suggestions.push(createSuggestion(col, monaco.languages.CompletionItemKind.Field, col, range, `${matchScore}_c_${col}`, '', `from ${table}`));
          });
        });

        return { suggestions };
      },
      triggerCharacters: ['.', '(', ',', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
    });

    // Dot-triggered provider (fast path for table.column completion)
    const dotDisposable = monaco.languages.registerCompletionItemProvider('sql', {
      triggerCharacters: ['.'],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };

        const currentLine = model.getLineContent(position.lineNumber);
        const textBefore = currentLine.substring(0, position.column - 1);
        const tableDotMatch = textBefore.match(/(\w+)\.(\w*)$/);

        if (tableDotMatch) {
          const tableName = tableDotMatch[1].toLowerCase();
          const columns = tableSchemasCache[tableName];
          if (!columns) return { suggestions: [] };

          return {
            suggestions: columns.slice(0, 50).map(col =>
              createSuggestion(col, monaco.languages.CompletionItemKind.Field, col, range, `0_${col}`, `Column from ${tableName}`)
            )
          };
        }

        return { suggestions: [] };
      }
    });

    return () => {
      disposable.dispose();
      dotDisposable.dispose();
    };
  }, [loadedTables, tableSchemas]);

  const loadDataset = useCallback(async (dataset, cachedBlob = null, totalDatasets = 0, tablePrefix = '') => {
    if (!dbReady) {
      setError("Database not ready yet. Please wait...");
      return;
    }

    setLoadingDataset(dataset.name);
    setError(null);

    try {
      // ROBUST ISOLATION STRATEGY:
      // 1. Create unique internal table name: {bucket}_{filename}
      // 2. Create user-facing VIEW: {filename} -> SELECT * FROM {internal_table}
      // This allows multiple 'orders' tables to exist (different buckets), 
      // with the active view controlling which one is visible as 'orders'.

      const baseTableName = dataset.name.replace(/\.(csv|parquet|json)$/, '').replace(/[^a-zA-Z0-9_]/g, '_');

      // Sanitized bucket name for prefix
      const bucketPrefix = dataset.bucket.replace(/[^a-zA-Z0-9_]/g, '_');
      const uniqueInternalTableName = `${bucketPrefix}_${baseTableName}`;

      // The user-facing name (e.g. 'orders') 
      // Priority: 
      // 1. Explicit Alias (dataset.tableAlias) -> for remapping users_v2 to users
      // 2. Table Prefix (tablePrefix) -> for namespacing (e.g. intro_orders)
      // 3. Filename base (baseTableName) -> default
      const visibleTableName = dataset.tableAlias
        ? dataset.tableAlias
        : tablePrefix ? `${tablePrefix}${baseTableName}` : baseTableName;

      const cacheKey = `${dataset.bucket}/${dataset.name}`;

      console.log('🔍 Loading dataset with isolation:', {
        file: dataset.name,
        bucket: dataset.bucket,
        internalTable: uniqueInternalTableName,
        visibleView: visibleTableName
      });

      // Check if UNIQUE table exists
      try {
        const existingTable = await conn.query(`SELECT * FROM ${uniqueInternalTableName} LIMIT 1`);
        if (existingTable && existingTable.numRows !== undefined) {
          console.log('✅ Internal table exists:', uniqueInternalTableName);

          // Force update the VIEW to point to this table (in case it was re-pointed)
          await conn.query(`CREATE OR REPLACE VIEW ${visibleTableName} AS SELECT * FROM ${uniqueInternalTableName}`);
          console.log('👁️ View updated:', visibleTableName, '->', uniqueInternalTableName);

          // Fetch schema from the VIEW or Table
          const schemaResult = await conn.query(`DESCRIBE ${visibleTableName}`);
          const columns = schemaResult.toArray().map(col => col.column_name);

          setTableSchemas(prev => ({ ...prev, [visibleTableName]: columns }));

          const sampleResult = await conn.query(`SELECT * FROM ${visibleTableName} LIMIT 3`);
          const sampleRows = sampleResult.toArray();

          setTableSampleData(prev => ({ ...prev, [visibleTableName]: sampleRows }));

          setLoadedTables(prevTables => {
            const newTables = new Set([...prevTables, visibleTableName]);
            if (newTables.size === totalDatasets) setAllTablesLoaded(true);
            return newTables;
          });

          setLoadingDataset(null);
          return;
        }
      } catch (e) {
        // Table doesn't exist, proceed to load
      }

      let fileBuffer;

      // Try to use cached blob first
      if (cachedBlob) {
        console.log('✨ Using cached data for:', dataset.name);
        fileBuffer = await cachedBlob.arrayBuffer();
      } else {
        // Fetch from Supabase
        const { data } = supabase.storage
          .from(dataset.bucket)
          .getPublicUrl(dataset.name);

        if (!data || !data.publicUrl) throw new Error(`Failed to get URL for ${dataset.name}`);

        const response = await fetch(data.publicUrl);
        if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);

        const blob = await response.blob();
        await setCachedData(cacheKey, blob);
        fileBuffer = await blob.arrayBuffer();
      }

      // Register file
      await db.registerFileBuffer(dataset.name, new Uint8Array(fileBuffer));

      // Create UNIQUE Internal Table
      if (dataset.name.endsWith('.csv')) {
        await conn.query(`CREATE OR REPLACE TABLE ${uniqueInternalTableName} AS SELECT * FROM read_csv_auto('${dataset.name}')`);
      } else if (dataset.name.endsWith('.parquet')) {
        await conn.query(`CREATE OR REPLACE TABLE ${uniqueInternalTableName} AS SELECT * FROM read_parquet('${dataset.name}')`);
      } else if (dataset.name.endsWith('.json')) {
        await conn.query(`CREATE OR REPLACE TABLE ${uniqueInternalTableName} AS SELECT * FROM read_json_auto('${dataset.name}')`);
      }

      // Create User-Facing VIEW
      await conn.query(`CREATE OR REPLACE VIEW ${visibleTableName} AS SELECT * FROM ${uniqueInternalTableName}`);
      console.log('✅ Created Isolated Table & View:', visibleTableName, '->', uniqueInternalTableName);

      setLoadedTables(prevTables => {
        const newTables = new Set([...prevTables, visibleTableName]);
        if (newTables.size === totalDatasets) setAllTablesLoaded(true);
        return newTables;
      });

      // Fetch Schema & Sample
      try {
        const schemaResult = await conn.query(`DESCRIBE ${visibleTableName}`);
        const columns = schemaResult.toArray().map(col => col.column_name);
        setTableSchemas(prev => ({ ...prev, [visibleTableName]: columns }));

        const sampleResult = await conn.query(`SELECT * FROM ${visibleTableName} LIMIT 3`);
        const sampleRows = sampleResult.toArray();
        setTableSampleData(prev => ({ ...prev, [visibleTableName]: sampleRows }));
      } catch (schemaErr) {
        console.error('Failed to fetch schema:', schemaErr);
      }
    } catch (err) {
      console.error('Dataset load error:', err);
      setError(`Failed to load dataset "${dataset.name}": ${err.message}`);
    } finally {
      setLoadingDataset(null);
    }
  }, [dbReady]);

  // Auto-load all datasets when database is ready
  useEffect(() => {
    const loadTables = async () => {
      console.log('🔍 Load check:', {
        dbReady,
        datasetsLength: datasets.length,
        loadedDatasetKey,
        currentDatasetKey,
        keysMatch: loadedDatasetKey === currentDatasetKey
      });

      // Only load if:
      // 1. Database is ready
      // 2. We have datasets to load
      // 3. We haven't loaded these specific datasets yet (prevents reloading on navigation)
      if (dbReady && datasets.length > 0 && loadedDatasetKey !== currentDatasetKey) {
        console.log('🚀 Starting to load', datasets.length, 'datasets');
        console.log('📊 Dataset key changed from', loadedDatasetKey, 'to', currentDatasetKey);

        try {
          // Immediately mark tables as not loaded to prevent query execution during reload
          setAllTablesLoaded(false);

          // Drop all existing tables ONLY when switching to different datasets
          console.log('🗑️ Clearing existing tables for fresh case load');
          const existingTables = await conn.query("SELECT table_name FROM information_schema.tables WHERE table_schema = 'main'");
          const tableNames = existingTables.toArray().map(row => row.table_name);

          for (const tableName of tableNames) {
            try {
              await conn.query(`DROP TABLE IF EXISTS ${tableName}`);
              console.log('🗑️ Dropped table:', tableName);
            } catch (dropErr) {
              console.warn('Failed to drop table:', tableName, dropErr);
            }
          }

          // Clear loaded tables state
          setLoadedTables(new Set());
          setTableSchemas({});
          setTableSampleData({});

          const totalDatasets = datasets.length;
          // Load all tables (using cache if available)
          // Cache key includes DATA_VERSION to automatically invalidate when data is updated
          // Load all tables in parallel (faster and tolerant of partial failures)
          // Cache key includes DATA_VERSION to automatically invalidate when data is updated
          const loadPromises = datasets.map(async (dataset) => {
            const cacheKey = `v${DATA_VERSION}_${dataset.bucket}/${dataset.name}`;
            const cachedBlob = await getCachedData(cacheKey);
            const tablePrefix = dataset.tablePrefix || '';
            return loadDataset(dataset, cachedBlob, totalDatasets, tablePrefix);
          });

          const results = await Promise.allSettled(loadPromises);

          // Check for critical failures
          const failures = results.filter(r => r.status === 'rejected');
          if (failures.length > 0) {
            console.error('⚠️ Some tables failed to load during init:', failures);
          }

          // Double check if 'users' table exists for Intro case
          if (currentDatasetKey.includes('INTRO') || currentDatasetKey.includes('Primer')) {
            try {
              await conn.query('SELECT count(*) FROM users');
            } catch (e) {
              console.error('🚨 CRITICAL: Users table missing after load sequence!', e);
              // Try emergency reload of just users?
            }
          }

          console.log('✅ All datasets processed');
          setAllTablesLoaded(true);
          setLoadedDatasetKey(currentDatasetKey); // Mark these datasets as loaded
        } catch (err) {
          console.error("Error loading tables:", err);
        }
      } else if (dbReady && datasets.length > 0 && loadedDatasetKey === currentDatasetKey) {
        console.log('✨ Datasets already loaded, skipping reload');
        // Datasets are already loaded, just ensure the UI state is correct
        if (!allTablesLoaded) {
          setAllTablesLoaded(true);
        }
      }
    };

    loadTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbReady, datasets, currentDatasetKey, loadedDatasetKey, loadDataset]);

  const executeQuery = async () => {
    // Check both React state AND actual connection refs
    // (keyboard handler checks dbRef/connRef, button should too for consistency)
    if (!dbReady || !allTablesLoaded || !dbRef.current || !connRef.current) {
      const msg = !dbReady ? "Database Engine is still initializing..."
        : !allTablesLoaded ? "Tables are still loading..."
          : "Database connection lost. Please refresh the page.";
      setError(`${msg}`);
      return;
    }

    // Prevent concurrent query execution (can cause DuckDB connection issues)
    if (loading) {
      return;
    }

    // Determine which query to execute:
    // 1. If text is selected, use the selection
    // 2. Otherwise, find the query at cursor position (between semicolons)
    let queryToExecute = query; // Default to full editor content

    const editor = editorRef.current;
    if (editor) {
      const model = editor.getModel();
      const selection = editor.getSelection();

      // Check if there's a non-empty selection
      if (selection && !selection.isEmpty()) {
        queryToExecute = model.getValueInRange(selection);
      } else {
        // No selection - find query at cursor position
        const cursorPosition = editor.getPosition();
        if (cursorPosition && model) {
          const fullText = model.getValue();
          // Convert line/column position to offset
          const cursorOffset = model.getOffsetAt(cursorPosition);
          queryToExecute = getQueryAtCursor(fullText, cursorOffset);
        }
      }
    }

    // Validate query - block destructive operations
    const destructiveKeywords = ['DROP', 'DELETE', 'UPDATE', 'ALTER', 'TRUNCATE', 'INSERT'];

    for (const keyword of destructiveKeywords) {
      // Check for keyword as whole word (not part of another word)
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      if (regex.test(queryToExecute)) {
        setError(`❌ Blocked: ${keyword} commands are not allowed. This playground is for SELECT queries only.`);
        return;
      }
    }

    if (!queryToExecute || !queryToExecute.trim()) {
      setError("No query to execute. Write a query or select text to run.");
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setExecutionTime(null);
    setShowChart(false); // Reset chart view when new query runs
    setActiveTab('results'); // Switch effectively immediately when query starts

    const start = performance.now();

    // Debug: Log query execution details
    console.log('🔍 Button executeQuery:', {
      queryToExecute: queryToExecute.substring(0, 100),
      connRefExists: !!connRef.current,
      dbRefExists: !!dbRef.current
    });

    try {
      const result = await connRef.current.query(queryToExecute);
      const end = performance.now();
      const duration = ((end - start) / 1000).toFixed(2);

      // SANITIZATION: Handle duplicate column names (common in JOINs) and BigInts
      // The DuckDB-WASM Proxy can crash React if multiple columns have the same name (e.g. user_id)
      const sanitizedResults = result.toArray().map(row => {
        const cleanRow = {};
        const seenKeys = new Set();

        // We iterate using the schema to get exact column order and names
        // Note: result.schema.fields is reliable for column metadata
        if (result.schema && result.schema.fields) {
          for (const field of result.schema.fields) {
            let key = field.name;
            // Access value - if duplicate keys exist, this usually gets the first one, 
            // but we can't easily access by index on the proxy object safely without internals.
            // However, for visualization, getting the first value is acceptable fallback 
            // if we avoid the crash.
            let val = row[key];

            // Handle duplicates by appending suffix
            if (seenKeys.has(key)) {
              let counter = 1;
              while (seenKeys.has(`${key}_${counter}`)) counter++;
              key = `${key}_${counter}`;
            }
            seenKeys.add(key);

            // Convert BigInt to Number for React safety
            if (typeof val === 'bigint') val = Number(val);

            cleanRow[key] = val;
          }
        } else {
          // Fallback if schema is missing (unlikely)
          return JSON.parse(JSON.stringify(row, (key, value) =>
            typeof value === 'bigint' ? Number(value) : value
          ));
        }

        return cleanRow;
      });

      setResults(sanitizedResults);
      setExecutionTime(duration);
      // toast.success(`Query Executed (${duration}s)`);

      const numRows = sanitizedResults.length; // Get number of rows from sanitized results
      posthog.capture('sql_query_executed', {
        status: 'success',
        duration: duration,
        rows: numRows,
        query_length: queryToExecute.length
      });

    } catch (err) {
      console.error(err);
      setError(err.message);
      posthog.capture('sql_query_executed', {
        status: 'error',
        error: err.message,
        query_length: queryToExecute.length
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChart = () => {
    setShowChart(prev => !prev);
  };

  // ── Drag-to-resize handlers ──────────────────────────────────────────────
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
    dragStartHeight.current = editorHeight;
    isDragging.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, [editorHeight]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const delta = clientY - dragStartY.current;
      const newH = Math.min(600, Math.max(120, dragStartHeight.current + delta));
      setEditorHeight(newH);
    };
    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
  }, []);

  return (
    <div className="w-full h-full flex flex-col min-w-0">


      {/* Main Editor Area - Full Width - FLEX LAYOUT */}
      <div className="w-full flex flex-col h-full min-w-0">
        {/* SQL Query Editor - FIXED HEIGHT */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex-shrink-0 transition-colors duration-300">
          <div className="bg-gradient-to-r from-primary-dark to-primary dark:from-slate-800 dark:to-slate-900 px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold text-lg">SQL Query Editor</h3>
            </div>
            <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${allTablesLoaded ? 'bg-green-500' : 'bg-yellow-500'} text-white`}>
              {allTablesLoaded
                ? '● Ready'
                : (!dbReady
                  ? '○ Initializing Engine...'
                  : `○ Loading Data... ${loadedTables.size}/${datasetFiles.length}`
                )
              }
            </span>
          </div>
          <div className="border-b border-gray-200 relative">
            <div
              ref={containerRef}
              style={{ height: editorHeight }}
              className="w-full overflow-hidden"
            >
              <Editor
                height="100%"
                defaultLanguage="sql"
                defaultValue="-- Type your SQL query here\n-- Example: SELECT * FROM accounts LEFT JOIN orders ON accounts.id = orders.account_id"
                value={query}
                onChange={(value) => setQuery(value || '')}
                onMount={handleEditorDidMount}
                theme="sql-custom"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                  // Enable suggestions while typing
                  quickSuggestions: {
                    other: true,
                    comments: false,
                    strings: false
                  },
                  quickSuggestionsDelay: 50,
                  suggestOnTriggerCharacters: true,
                  wordBasedSuggestions: false,
                  // Force suggestions to appear
                  acceptSuggestionOnCommitCharacter: true,
                  // Performance optimizations
                  folding: false,
                  glyphMargin: false,
                  renderLineHighlight: 'line',
                  smoothScrolling: false,
                  cursorBlinking: 'smooth',
                  // Autocomplete settings
                  suggest: {
                    showWords: false,
                    showSnippets: false,
                    snippetsPreventQuickSuggestions: false,
                    filterGraceful: true,
                    localityBonus: true,
                    shareSuggestSelections: false,
                    insertMode: 'replace',
                    preview: true,
                    previewMode: 'prefix',
                  },
                  acceptSuggestionOnEnter: 'off',  // Disable Enter for autocomplete
                  tabCompletion: 'on',  // Tab accepts suggestions
                }}
              />
            </div>
            {/* Action Buttons - Overlay at bottom-right */}
            <div className="absolute bottom-4 right-4 flex items-center gap-2 z-20">
              {/* standard Run Query Button */}
              <button
                id="sql-tour-run"
                onClick={executeQuery}
                disabled={loading || !dbReady}
                className={`px-4 py-2 rounded-md font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg text-sm ${loading || !dbReady
                  ? "bg-slate-600 text-slate-400 cursor-not-allowed"
                  : "bg-slate-700 hover:bg-slate-600 text-white"
                  }`}
                title={`${shortcutSymbol} + Enter to execute`}
              >
                {loading && !isVerifying ? (
                  <>
                    <FaSpinner className="animate-spin" size={12} />
                    Running...
                  </>
                ) : (
                  <>
                    <FaPlay size={10} />
                    Run <span className="opacity-60 text-xs ml-0.5 font-sans">({shortcutSymbol} + Enter)</span>
                  </>
                )}
              </button>

              {/* Verify Button (if handler provided) */}
              {onVerify && (
                <button
                  id="sql-tour-verify"
                  onClick={onVerify}
                  disabled={loading || !dbReady || isVerifying}
                  className={`px-4 py-2 rounded-md font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg text-sm ${isCompleted
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-500 shadow-emerald-500/20'
                    : loading || !dbReady || isVerifying
                      ? "bg-blue-400 text-white/80 cursor-wait"
                      : "bg-blue-600 hover:bg-blue-500 text-white border border-blue-500"
                    }`}
                >
                  {isVerifying ? (
                    <>
                      <FaSpinner className="animate-spin" size={12} />
                      Verifying...
                    </>
                  ) : isCompleted ? (
                    <>
                      <FaCheckCircle size={12} />
                      Verify Again
                    </>
                  ) : (
                    <>
                      <FaPlay size={10} />
                      Run & Verify
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>



        {/* Drag handle */}
        <div
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          className="flex-shrink-0 h-2.5 flex items-center justify-center cursor-row-resize group select-none"
          title="Drag to resize editor"
        >
          <div className="w-12 h-1 rounded-full bg-gray-300 dark:bg-gray-600 group-hover:bg-orange-400 dark:group-hover:bg-orange-500 transition-colors duration-150" />
        </div>

        {/* Tabs & Content Area - Takes remaining space */}
        <div id="sql-tour-results" className="flex-1 overflow-hidden flex flex-col bg-white dark:bg-[#1e293b] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 transition-colors duration-300">
          {/* Tab Navigation - Horizontal Scrollable */}
          <div className="flex items-center border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#0f172a] overflow-x-auto custom-scrollbar">
            {/* 1. Fixed Query Results Tab */}
            <button
              onClick={() => setActiveTab('results')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-2 ${activeTab === 'results'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-[#1e293b]'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
            >
              <FaPlay size={10} className={activeTab === 'results' ? 'text-blue-500' : 'text-gray-400'} />
              Query Results
            </button>

            {/* 2. Vertical Separator */}
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-700 mx-2 flex-shrink-0"></div>

            {/* 3. Schema tables - tour target wraps icon + table tabs */}
            <div id="sql-tour-schema" className="flex items-center">
              {/* Classy Icon Separator for Available Tables */}
              <div className="flex items-center px-2 text-gray-300 dark:text-gray-600 flex-shrink-0 select-none" title="Available Tables">
                <FaDatabase size={12} />
              </div>

              {/* 4. Dynamic Table Tabs */}
              {datasets.map((dataset) => {
                const baseTableName = dataset.name.replace(/\.(csv|parquet|json)$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
                const tableName = dataset.tableAlias
                  ? dataset.tableAlias
                  : (dataset.tablePrefix ? `${dataset.tablePrefix}${baseTableName}` : baseTableName);
                const isLoading = loadingDataset === dataset.name;

                return (
                  <button
                    key={dataset.name}
                    onClick={() => setActiveTab(tableName)}
                    className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-2 ${activeTab === tableName
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-[#1e293b]'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                  >
                    <FaTable size={12} className={activeTab === tableName ? 'text-blue-500' : 'text-gray-400'} />
                    {tableName}
                    {isLoading && <FaSpinner className="animate-spin text-blue-500" size={12} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden relative bg-white dark:bg-[#1e293b]">
            {/* 1. Results View - shows "Query Results" title by default */}
            <div className={`absolute inset-0 flex flex-col ${activeTab === 'results' ? 'z-10' : 'z-0 hidden'}`}>
              <ResultsTable
                results={results}
                error={error}
                loading={loading}
                displayLimit={displayLimit}
                setDisplayLimit={setDisplayLimit}
                onToggleChart={handleToggleChart}
                showChart={showChart}
                title="Query Results"
                executionTime={executionTime}
              />
            </div>

            {/* 2. Table Sample Views - Lazy Rendered or Hidden */}
            {datasets.map((dataset) => {
              const baseTableName = dataset.name.replace(/\.(csv|parquet|json)$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
              const tableName = dataset.tableAlias
                ? dataset.tableAlias
                : (dataset.tablePrefix ? `${dataset.tablePrefix}${baseTableName}` : baseTableName);

              if (activeTab !== tableName) return null;

              const sampleData = tableSampleData[tableName] || [];

              return (
                <div key={`view-${tableName}`} className="absolute inset-0 flex flex-col z-10 animate-fadeIn">
                  <ResultsTable
                    results={sampleData}
                    displayLimit={100}
                    setDisplayLimit={() => { }}
                    onToggleChart={() => { }}
                    showChart={false}
                    title={tableName}
                    subtitle={`${sampleData.length} sample rows`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div >

  );
});

SQLEditor.displayName = 'SQLEditor';
export default SQLEditor;
